import { ReactElement, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import * as Sentry from "@sentry/react";
import { twMerge } from "tailwind-merge";

import { WarningIcon } from "~/common/components";
import Spinner from "~/common/components/ui/Spinner";

interface PdfDownloadLinkProps {
  document: ReactElement;
  fileName: string;
  className?: string;
}

export const PdfDownloadLink = ({
  document: pdfDocument,
  fileName,
  className,
}: PdfDownloadLinkProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const handleClick = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const blob = await pdf(pdfDocument).toBlob();

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (err) {
      const exportError =
        err instanceof Error ? err : new Error("Failed to export PDF");
      setError(exportError);
      Sentry.captureException(exportError);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isGenerating}
      title={error ? error.message : "Download PDF"}
      className={twMerge(
        "flex items-center justify-center gap-2 rounded-md border border-black bg-neutral-25 px-4 py-2",
        "text-sm font-medium text-black hover:bg-neutral-150 focus:bg-neutral-150 focus:outline-none",
        isGenerating && "border-gray-400 text-gray-400",
        error && "text-red-400",
        className,
      )}
    >
      {isGenerating && <Spinner className="h-5 w-5" />}
      {error && <WarningIcon className="text-red-400" />}
      Export PDF
    </button>
  );
};
